<script setup lang="ts">
import { ref } from 'vue'
import { mdiClose, mdiLoading } from '@mdi/js'
import type { Validate } from '@/shared/types/validation.types'
import BaseButton from '@/shared/components/button/BaseButton.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import LabelField from '../label/LabelField.vue'

interface Props {
  id: string
  label: string
  required?: boolean
  readonly?: boolean
  disabled?: boolean
  saving?: boolean
  validate?: Validate
}

interface Emits {
  (e: 'clear'): void
  (e: 'blur'): void
  (e: 'input'): void
}

defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<string>()
const isInitialValue = ref(model.value !== undefined)
const localValue = ref(model.value ?? '')

const onClear = (): void => {
  model.value = isInitialValue.value ? '' : undefined
  
  emits('clear')
}

const onBlur = (): void => {
  emits('blur')
}

function onInput(e: Event) {
  const target = e.target as HTMLInputElement
  const value = target.value.trim()
  const normalized = value === '' ? (isInitialValue.value ? '' : undefined) : value
  model.value = normalized
  localValue.value = value
  emits('input')
}
</script>

<template>
  <div class="w-full">
    <LabelField
      :id="id"
      :text="label"
      :required="required"
      :validate="validate"
      :disabled="disabled"
    />
    <div class="relative flex items-center">
      <input
        :id="id"
        class="peer block w-full text-sm p-2 pe-8 outline-none border disabled:bg-gray-100 read-only:border-dashed read-only:text-gray-700 focus:ring-2 text-black dark:text-white dark:border-gray-400"
        :class="
          validate?.isDirty && validate?.isInvalid
            ? 'border-error focus:ring-error'
            : 'border-gray-300 focus:ring-black dark:'
        "
        autocomplete="off"
        :value="model"
        :required="required"
        :readonly="readonly"
        :disabled="disabled"
        @input="(e) => onInput(e)"
        @blur="onBlur()"
      />
      <BaseButton
        v-if="model && !disabled && !readonly && !saving"
        :title="$t('common.clear')"
        :aria-label="$t('common.clear')"
        class="absolute top-2 right-2"
        @click="onClear()"
      >
        <SvgIcon class="fill-gray-700 h-5 w-5" :path="mdiClose" />
      </BaseButton>
      <div v-if="saving" class="animate-spin absolute right-2">
        <SvgIcon :path="mdiLoading" class="fill-gray-700 h-5 w-5" />
      </div>
    </div>
  </div>
</template>
